import { routes } from "@/constants/url";
import { supabase } from "@/lib/supabase";
import { PasswordSchema } from "@/utils/schema";
import { useToastMessage } from "@backpac/monica";
import { Button, Form, FormControl, FormField, FormItem, FormLabel, FormMessage, Typography } from "@backpac/monica";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";
import { PasswordInput } from "../components/password-input";

const ResetPasswordSchema = z
  .object({
    newPassword: PasswordSchema,
    confirmPassword: PasswordSchema,
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordForm = z.infer<typeof ResetPasswordSchema>;

export function ResetPasswordPage() {
  const { error, success } = useToastMessage();
  const navigate = useNavigate();

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    const response = await supabase.auth.updateUser({
      password: data.newPassword,
    });

    if (response.error) {
      return error("Failed to reset password");
    }

    success("Password reset was successful!");

    navigate(routes.AUTH.signin);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 w-full">
        <Typography asChild size="xl" font="unbounded">
          <h1>Reset password</h1>
        </Typography>
        <Typography asChild size="sm" weight={400}>
          <p>Please create a password with at least 8 characters, including special characters (e.g. !?.$)</p>
        </Typography>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
          <FormField
            control={form.control}
            name="newPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            className="mt-2 h-12 w-full"
            disabled={!form.formState.isValid || form.formState.isSubmitting}
          >
            Reset password
          </Button>
        </form>
      </Form>
    </div>
  );
}
